import { useCallback, useRef, useEffect } from 'react';
import { debounce } from 'lodash';
import {
  V1PreviewTradeFeeResponse,
  TradeServiceApi,
  V1MarketSide,
} from '@fintronners/react-api/src';

type UsePreviewTradeFeeOptions = {
  onSuccess?: (data: V1PreviewTradeFeeResponse) => void;
  onError?: (error: Error) => void;
  onRequestStart?: () => void;
  onRequestEnd?: () => void;
  onRequestInvoked?: () => void;
  onRequestSkipped?: () => void;
  withDebounce?: boolean;
  shouldSkip?: boolean;
  apiService: TradeServiceApi;
};

type UsePreviewTradeFeeReturn = {
  getTradeFee: ({
    assetUid,
    amount,
  }: UsePreviewTradeFeeParams) => Promise<V1PreviewTradeFeeResponse | Error | undefined> | void;
  abortGetTradeFee: () => void;
};

type UsePreviewTradeFeeParams = {
  assetUid: string;
  amount: string;
  side?: V1MarketSide;
};

const DEFAULT_DEBOUNCE_TIME = 1000;

/**
 * Hook for previewing trade fees
 *
 * @param options - Optional callbacks and configuration
 * @param options.onSuccess - Callback called when trade fee is successfully fetched
 * @param options.onError - Callback called when an error occurs
 * @param options.onRequestStart - Callback called when the API request starts
 * @param options.onRequestEnd - Callback called when the API request ends
 * @param options.onRequestInvoked - Callback called when the API request is invoked
 * @param options.onRequestSkipped - Callback called when the API request is skipped
 * @param options.withDebounce - Whether to debounce the API calls. When enabled, loading state shows immediately on user input, but API call is delayed.
 * @param options.shouldSkip - Whether to skip the API call.
 * @param options.apiService - The trade API service to use.
 * @returns Object containing:
 * - getTradeFee: Function to fetch trade fee preview with parameters
 */
const usePreviewTradeFee = ({
  onSuccess,
  onError,
  onRequestStart,
  onRequestEnd,
  onRequestInvoked,
  onRequestSkipped,
  withDebounce = false,
  shouldSkip = false,
  apiService,
}: UsePreviewTradeFeeOptions): UsePreviewTradeFeeReturn => {
  const tradeApiService = apiService;
  const abortControllerRef = useRef<AbortController | undefined>(undefined);

  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  const onRequestStartRef = useRef(onRequestStart);
  const onRequestEndRef = useRef(onRequestEnd);
  const onRequestInvokedRef = useRef(onRequestInvoked);
  const onRequestSkippedRef = useRef(onRequestSkipped);

  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    onRequestStartRef.current = onRequestStart;
  }, [onRequestStart]);

  useEffect(() => {
    onRequestEndRef.current = onRequestEnd;
  }, [onRequestEnd]);

  useEffect(() => {
    onRequestInvokedRef.current = onRequestInvoked;
  }, [onRequestInvoked]);

  useEffect(() => {
    onRequestSkippedRef.current = onRequestSkipped;
  }, [onRequestSkipped]);

  const invokeRequest = useCallback(
    async ({ assetUid, amount, side }: UsePreviewTradeFeeParams) => {
      onRequestStartRef.current?.();

      try {
        const { data: feesAndCommissionsResponse } =
          await tradeApiService.tradeServicePreviewTradeFee(
            {
              assetUid,
              amount,
              side, // defaults to BUY if undefined on the server side
            },
            {
              signal: abortControllerRef.current?.signal,
            },
          );

        onSuccessRef.current?.(feesAndCommissionsResponse);
        return feesAndCommissionsResponse;
      } catch (err) {
        if (err instanceof Error && (err.name === 'CanceledError' || err.name === 'AbortError')) {
          return;
        }

        const errorToSet = err instanceof Error ? err : new Error('Unknown error occurred');

        onErrorRef.current?.(errorToSet);
        return errorToSet;
      } finally {
        onRequestEndRef.current?.();
        abortControllerRef.current = undefined;
      }
    },
    [tradeApiService],
  );

  const invokeDebouncedRequest = useCallback(
    debounce(invokeRequest, DEFAULT_DEBOUNCE_TIME, {
      leading: !withDebounce,
      trailing: withDebounce,
    }),
    [invokeRequest, withDebounce],
  );

  const abortRequest = useCallback(() => {
    const isPending = Boolean(abortControllerRef.current);

    if (!isPending) {
      return;
    }

    abortControllerRef.current?.abort();
    abortControllerRef.current = undefined;
    invokeDebouncedRequest.cancel();
    onRequestEndRef.current?.();
  }, [invokeDebouncedRequest]);

  const getTradeFee = useCallback(
    (...args: Parameters<typeof invokeRequest>) => {
      abortRequest();

      if (shouldSkip) {
        onRequestSkippedRef.current?.();
        return;
      }

      onRequestInvokedRef.current?.();

      abortControllerRef.current = new AbortController();

      return invokeDebouncedRequest(...args);
    },
    [invokeDebouncedRequest, shouldSkip],
  );

  return {
    getTradeFee,
    abortGetTradeFee: abortRequest,
  };
};

export default usePreviewTradeFee;
